'use client';

import type { KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { FiCheck, FiX } from 'react-icons/fi';

import { ActionIcon, Group, TextInput } from '@highlight-cp/core-ui';

export interface InlineRenameFieldProps {
  /** Names the field — it stands in for a heading, so it has no visible label. */
  ariaLabel: string;
  /** Why `value` can't stand. Shown until the draft moves off `value`, which it no longer describes. */
  error?: string;
  maxLength?: number;
  /** Called with the trimmed name. Only reachable while the draft is non-blank. */
  onCommit: (name: string) => void;
  onCancel: () => void;
  /** The current name, which the draft starts from. */
  value: string;
}

/**
 * ### InlineRenameField
 *
 * @description
 * The editing state of a rename-in-place: a text field with cancel and confirm buttons, for swapping
 * in where a title normally sits. Holds its own draft, so the caller only hears the committed name;
 * Enter commits and Escape cancels, and confirm is unreachable while the draft is blank. An `error`
 * outlines the field and sits under it until the user edits the name.
 *
 * @example
 * ```tsx
 * {isRenaming ? (
 *   <InlineRenameField
 *     ariaLabel={t('Test group name')}
 *     value={testGroup.name}
 *     onCommit={handleRename}
 *     onCancel={stopRenaming}
 *   />
 * ) : (
 *   <Title order={2}>{testGroup.name}</Title>
 * )}
 * ```
 *
 * @param props - {@link InlineRenameFieldProps}
 * @returns React component
 */
export const InlineRenameField = ({
  ariaLabel,
  error,
  maxLength,
  onCommit,
  onCancel,
  value,
}: InlineRenameFieldProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = draft.trim();
  const isUnedited = trimmed === value.trim();

  // Focused on mount, because what opened this — a menu item, a rename button — is gone by now, and
  // leaving focus where it was strands a keyboard user outside the field they asked for. Done with a
  // ref rather than `autoFocus`, which fires on any mount, wanted or not.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const commit = () => {
    if (trimmed) {
      onCommit(trimmed);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      // Committing is the whole action — without this an enclosing form would submit as well.
      event.preventDefault();
      commit();
      return;
    }
    if (event.key === 'Escape') {
      // Stops here rather than reaching an enclosing modal or drawer, which would close on Escape.
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <TextInput
      ref={inputRef}
      aria-label={ariaLabel}
      error={isUnedited ? error : undefined}
      // The buttons sit beside the input itself, so an error opens under the whole row.
      inputContainer={(input) => (
        <Group gap="xs" wrap="nowrap">
          {input}
          <ActionIcon aria-label={t('Cancel')} onClick={onCancel} variant="default">
            <FiX aria-hidden />
          </ActionIcon>
          <ActionIcon aria-label={t('Save')} disabled={!trimmed} onClick={commit} variant="filled">
            <FiCheck aria-hidden />
          </ActionIcon>
        </Group>
      )}
      maxLength={maxLength}
      // On the field alone: from the buttons these keys are their own activation, and an Enter
      // with Cancel focused would commit the rename the user was abandoning.
      onKeyDown={handleKeyDown}
      onChange={(event) => setDraft(event.currentTarget.value)}
      value={draft}
    />
  );
};
